/**
 * The assistant's face, whatever draws it. Every face gets the same inputs and is chosen the same way:
 *  - FaceState  what the assistant is doing (faceStateOf: the voice, or the conversation in a replay);
 *  - FaceCue    the emoji cues (cues.ts);
 *  - the brand colours, and the box it is given.
 *
 * The kinds:
 *  - musetalk   the video head (avatar/ MuseTalk): the main one. Needs the voice and the avatar service.
 *  - lowpoly    the three.js head (avatar-lowpoly/, :5666, `?bg3d=1`): a whole-screen page.
 *  - idle       the stage's avatar breathing and blinking, not talking: its idle loop with the green screen
 *               keyed out (stage/catalog.ts, KeyedVideo.tsx), on whatever background; there when the build
 *               has an avatar.
 *  - particles  the particle orb, drawn here: always there.
 * Which one shows: Settings (Appearance.face),
 * `auto` = what the place asks for; a face this screen does not have gives way to the theme's stand-in
 * (Appearance.faceFallback: Classico the idle avatar, Essenziale the orb).
 */
import type { ComponentType } from 'react';
import type { Box } from '../layout/geometry';
import type { VoiceState } from '../voice/controller';
import type { OrbState } from './particles/orb-state';

export const FACE_KINDS = ['musetalk', 'lowpoly', 'idle', 'particles'] as const;
export type FaceKind = (typeof FACE_KINDS)[number];
export type FaceChoice = 'auto' | FaceKind;

/** What the assistant is doing, the same for every face. */
export type FaceState = OrbState;

/** The face the platform draws from nothing: always available. */
export const BUILTIN_FACES: readonly FaceKind[] = ['particles'];
export const FACE_FALLBACK: FaceKind = 'particles';

/** Whether this screen has a face: the outside ones its renderer draws, the idle one when the stage has an avatar. */
export function facesHere(renderer: Pick<FaceRenderer, 'has'> | null, hasAvatar: boolean): (kind: FaceKind) => boolean {
  return (kind) => (kind === 'idle' ? hasAvatar : renderer?.has(kind) ?? false);
}

/**
 * The faces drawn outside the platform (they need the voice, a service, a flag), given by the app:
 * which ones this screen could have, and what draws them in a box. Having one is not enough: it says
 * when it is live (`onLive`: MuseTalk at its first frame), and until then the place shows its stand-in.
 */
export interface FaceRenderer {
  has(kind: FaceKind): boolean;
  Render: ComponentType<{ box: Box; kind: FaceKind; onLive(live: boolean): void }>;
}

/**
 * The face that shows: the chosen one (or, on `auto`, the asked one) if this screen has it, else the
 * first of the place's fallbacks it has, else the orb.
 */
export function resolveFace(choice: FaceChoice, asked: FaceKind, available: (kind: FaceKind) => boolean, fallbacks: readonly FaceKind[] = []): FaceKind {
  const has = (kind: FaceKind) => BUILTIN_FACES.includes(kind) || available(kind);
  return [choice === 'auto' ? asked : choice, ...fallbacks].find(has) ?? FACE_FALLBACK;
}

/**
 * The face state from the voice (live kiosk), or from the conversation alone (a replay, an operator:
 * no voice here, but an answer being prepared is still "thinking").
 */
export function faceStateOf(voice: VoiceState | null, thinking: boolean): FaceState {
  if (!voice) return thinking ? 'thinking' : 'idle';
  if (voice.error) return 'error';
  if (voice.connection === 'connecting') return 'connecting';
  if (voice.connection === 'error') return 'error';
  switch (voice.activity) {
    case 'listening': return 'listening';
    case 'processing': return 'thinking';
    case 'speaking': return 'speaking';
    default: return thinking ? 'thinking' : 'idle';
  }
}
